import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, animate, useInView, AnimatePresence } from "framer-motion";
import { ArrowUpRight, FileText, Award, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp, MaskedLine, Eyebrow, EASE_OUT } from "@/components/Reveal";

const CountUp = ({ to, suffix = "", testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} data-testid={testId}>
      {val.toLocaleString("en-IN")}{suffix}
    </span>
  );
};

const CEO_CERT_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_executive-hire-pro/artifacts/pn0rjuzf_1662643933956.jpeg";
const CEO_CERT_PDF = "https://customer-assets-wrfwihn1.emergentagent.net/job_executive-hire-pro/artifacts/zva92ke5_Ally-eXecutive%20final.pdf";
const GC_CERT_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_executive-hire-pro/artifacts/mgwf58u1_Doc1_page-0001.jpg";
const GC_CERT_PDF = "https://customer-assets-wrfwihn1.emergentagent.net/job_executive-hire-pro/artifacts/97zkggqz_Great%20companies.pdf";

const STORIES = [
  {
    practice: "Retained Search",
    title: "Chief Technology Officer — PE-backed InsurTech",
    context: "A confidential, mission-critical technology leadership mandate across three markets.",
    outcome: "140+ leaders mapped; a structured shortlist of five; offer accepted inside nine weeks. The placed CTO now leads the core-platform modernization program.",
    metric: "9 wks",
    metricLabel: "mandate to accepted offer",
  },
  {
    practice: "Executive Search",
    title: "Country Head, India — Global MarTech Platform",
    context: "First India leadership hire for a global marketing technology company entering the market.",
    outcome: "Placed a country leader who established the entity, built the go-to-market motion and hired a 25-person team within the first year.",
    metric: "1st",
    metricLabel: "leadership hire in market",
  },
  {
    practice: "Permanent Hiring",
    title: "GCC Engineering Ramp-Up — US SaaS Major",
    context: "Standing up a new Global Capability Center engineering org under aggressive timelines.",
    outcome: "40+ specialist engineering hires closed across two quarters — architecture, cloud, data and platform roles — without compromising bar.",
    metric: "40+",
    metricLabel: "hires in two quarters",
  },
  {
    practice: "Executive Search · RoleSense™",
    title: "VP Engineering — Healthcare Technology",
    context: "A complex technology environment where keyword matching kept surfacing the wrong profiles.",
    outcome: "RoleSense™ Technical Fitment Intelligence narrowed 300 profiles to six contextual finalists; the client hired from the first shortlist.",
    metric: "6",
    metricLabel: "finalists from 300 profiles",
  },
  {
    practice: "Startup HR Advisory",
    title: "People Function Build-Out — Series B SaaS Startup",
    context: "Founder-led hiring had hit its ceiling; no HR foundation, no structure, rising attrition.",
    outcome: "Built HR policies, salary bands, competency frameworks and performance systems — scaling the company from 40 to 200 employees.",
    metric: "40→200",
    metricLabel: "employees on the new HR foundation",
  },
  {
    practice: "Contract & Staff Augmentation",
    title: "Specialist Bench — Digital Transformation Program",
    context: "A transformation program needed niche technology specialists faster than permanent hiring allowed.",
    outcome: "25 contract specialists deployed within 30 days across cloud, integration and QA — program milestones met without permanent headcount.",
    metric: "30 days",
    metricLabel: "to a 25-specialist bench",
  },
];

const TESTIMONIALS = [
  {
    quote: "We've been working with the Ally team for different mandates for over 4 years. They successfully assisted us in exploring the market and securing the right candidate — gathering information about the role and organization before they began the search. We recommend them for any niche roles.",
    name: "Thiyagarajan Selvaraj",
    role: "Manager TA · AdTech · Client",
  },
  {
    quote: "I was contacted for a Senior L&D role, and the initial discussion was very clear and focussed. Shan was in touch at every step — through the interview process, the BGV and after joining — to ensure I had settled in comfortably. Very professionally handled with a personalized touch.",
    name: "Anuradha",
    role: "Sr. Manager, Learning & Development · Global AdTech Brand · Placed Candidate",
  },
  {
    quote: "Excellent across speed of delivery, quality of candidates and overall service — we would recommend Ally's services to others and re-use them ourselves.",
    name: "Aanchal G",
    role: "Sr. Manager TA · Global AdTech, Bangalore · Client",
  },
  {
    quote: "Right from setting expectations about the role, explaining the skill-set requirements and interview patterns, to post-interview feedback — they were always a guide force. Efficient in doing things right, and effective in doing the right things.",
    name: "Shyam Jindhe",
    role: "Placed by Ally eXecutive",
  },
];

const RECOGNITION = [
  {
    img: CEO_CERT_IMG,
    pdf: CEO_CERT_PDF,
    body: "The CEO Magazine",
    title: "20 Most Valuable Consulting Companies in India — 2022",
    id: "ceo-magazine",
  },
  {
    img: GC_CERT_IMG,
    pdf: GC_CERT_PDF,
    body: "Great Companies — Global Business Award",
    title: "Top Global Business of the Year 2022 · Recruitment Category",
    id: "great-companies",
  },
];

const SuccessStories = () => {
  const [tIndex, setTIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTIndex((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, []);
  const t = TESTIMONIALS[tIndex];
  return (
  <div data-testid="success-stories-page">
    {/* HERO */}
    <section className="border-b border-line pt-36 lg:pt-44" data-testid="stories-hero">
      <div className="mx-auto max-w-7xl px-6 pb-16 lg:px-10 lg:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <Eyebrow>Success Stories</Eyebrow>
        </motion.div>
        <h1 className="mt-6 max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>The measure of recruitment is</MaskedLine>
          <MaskedLine delay={0.4}>
            not how many CVs we send. <span className="italic text-gold-dark">It is outcomes.</span>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE_OUT }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-ink-muted sm:text-lg"
        >
          A selection of mandates and advisory engagements. Client identities
          are withheld in line with the confidentiality our search work demands.
        </motion.p>
      </div>
    </section>

    {/* TRACK RECORD */}
    <section className="border-b border-line bg-ink py-16 text-paper lg:py-20" data-testid="track-record-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 sm:grid-cols-2 lg:px-10">
        <FadeUp>
          <p className="font-serif text-5xl tracking-tight text-gold sm:text-6xl"><CountUp to={200} suffix="+" testId="stat-placements" /></p>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-paper/60">
            Professionals successfully placed across mid and leadership capacity.
          </p>
        </FadeUp>
        <FadeUp delay={0.12}>
          <p className="font-serif text-5xl tracking-tight text-gold sm:text-6xl"><CountUp to={4000} suffix="+" testId="stat-ready-reckoner" /></p>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-paper/60">
            Ready Reckoner — professionals mapped and available for quick deployment.
          </p>
        </FadeUp>
      </div>
    </section>

    {/* STORIES */}
    <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28" data-testid="stories-grid-section">
      <div className="grid gap-px border border-line bg-line md:grid-cols-2">
        {STORIES.map((s, i) => (
          <FadeUp key={s.title} delay={0.05 * (i % 2)} className="group flex flex-col bg-paper p-9 transition-colors duration-500 hover:bg-paper-deep lg:p-11">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold-dark">{s.practice}</p>
            <h2 className="mt-4 font-serif text-xl text-ink sm:text-2xl">{s.title}</h2>
            <p className="mt-3 text-sm italic leading-relaxed text-ink-faded">{s.context}</p>
            <p className="mt-4 flex-1 text-sm leading-relaxed text-ink-muted">{s.outcome}</p>
            <div className="mt-8 flex items-baseline gap-3 border-t border-line pt-6">
              <p className="font-serif text-4xl text-gold-dark">{s.metric}</p>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faded">{s.metricLabel}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.1} className="mt-8">
        <p className="text-xs text-ink-faded" data-testid="stories-confidentiality-note">
          * Client names and identifying details withheld under confidentiality agreements. References available during mandate discussions.
        </p>
      </FadeUp>
    </section>

    {/* TESTIMONIALS CAROUSEL */}
    <section className="border-b border-line py-20 lg:py-28" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <Eyebrow>What Clients & Candidates Say</Eyebrow>
        </FadeUp>
        <div className="relative mt-12 border border-line bg-paper-card" data-testid="testimonial-carousel">
          <div className="absolute -top-px left-10 h-[3px] w-16 bg-gold" aria-hidden />
          <div className="min-h-[300px] px-8 py-12 sm:px-12 lg:px-16 lg:py-16">
            <AnimatePresence mode="wait">
              <motion.figure
                key={tIndex}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: EASE_OUT }}
              >
                <blockquote className="max-w-4xl font-serif text-xl italic leading-relaxed text-ink sm:text-2xl" data-testid="testimonial-quote">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-8">
                  <p className="font-serif text-lg text-ink" data-testid="testimonial-name">{t.name}</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-dark" data-testid="testimonial-role">{t.role}</p>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>
          <div className="flex items-center justify-between border-t border-line px-8 py-5 sm:px-12 lg:px-16">
            <div className="flex gap-2.5">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setTIndex(i)}
                  aria-label={`Testimonial ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-2 transition-all duration-400 ${i === tIndex ? "w-8 bg-gold" : "w-2 bg-line-medium hover:bg-ink-faded"}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setTIndex((tIndex - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                aria-label="Previous testimonial"
                data-testid="testimonial-prev-button"
                className="border border-line-medium p-2.5 text-ink-muted transition-colors duration-300 hover:border-ink hover:text-ink"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setTIndex((tIndex + 1) % TESTIMONIALS.length)}
                aria-label="Next testimonial"
                data-testid="testimonial-next-button"
                className="border border-line-medium p-2.5 text-ink-muted transition-colors duration-300 hover:border-ink hover:text-ink"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* RECOGNITION */}
    <section className="border-y border-line bg-paper-deep py-20 lg:py-28" data-testid="recognition-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <Eyebrow>Recognition & Awards</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Recognized for the work behind the outcomes.
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          {RECOGNITION.map((r, i) => (
            <FadeUp key={r.id} delay={0.08 * i} className="border border-line-medium bg-paper-card p-7 lg:p-9" data-testid={`recognition-card-${r.id}`}>
              <div className="flex items-center gap-3">
                <Award className="h-5 w-5 text-gold-dark" />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold-dark">{r.body}</p>
              </div>
              <h3 className="mt-3 font-serif text-xl text-ink sm:text-2xl">{r.title}</h3>
              <div className="mt-6 overflow-hidden border border-line bg-paper">
                <img
                  src={r.img}
                  alt={`${r.body} — ${r.title}`}
                  className="max-h-80 w-full object-contain"
                  data-testid={`recognition-image-${r.id}`}
                />
              </div>
              <a
                href={r.pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-6 inline-flex items-center gap-2 border border-ink px-5 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
                data-testid={`recognition-pdf-${r.id}`}
              >
                <FileText className="h-4 w-4" />
                View Certificate (PDF)
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="bg-ink py-20 text-paper lg:py-28" data-testid="stories-cta-section">
      <div className="mx-auto max-w-7xl px-6 text-center lg:px-10">
        <FadeUp>
          <h2 className="mx-auto max-w-2xl font-serif text-2xl tracking-tight sm:text-3xl lg:text-4xl">
            Your mandate could be the <span className="italic text-gold">next story.</span>
          </h2>
          <Link
            to="/about-contact#contact"
            className="group mt-9 inline-flex items-center gap-2 border border-gold bg-gold px-8 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-transparent hover:text-gold"
            data-testid="stories-cta-button"
          >
            Start a Conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </FadeUp>
      </div>
    </section>
  </div>
  );
};

export default SuccessStories;
